'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { MarketReveal, U, Yl } from './collection-animation';
import { ShowcaseIcon } from './scene-primitives';
import { CompletionMark, BlurSwap } from './playback-controls';
import { LisiereLogo } from './reference-brands';
export function Ql(e, t, n, r = 38) {
  return t < n ? '' : e.slice(0, Math.floor((t - n) / r) + 1);
}
export function $l(e, t, n) {
  return Math.min(1, Math.max(0, (e - t) / (n - t)));
}
export function MarketImage({
  asset: e,
  zoom: t = 1,
  focus: n = '50% 40%',
  alt: r = '',
  className: i = '',
  imgStyle: a,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let o = !e.ready && e.width === e.height,
    s = o ? t * 1.085 : t;
  return (
    <span className={`relative block overflow-hidden ${i}`}>
      <img
        src={e.src}
        alt={translateShowcaseNode(showcaseCopy, r)}
        width={e.width}
        height={e.height}
        loading={'lazy'}
        decoding={'async'}
        draggable={!1}
        className={'block size-full object-cover'}
        style={{
          objectPosition: o ? '50% 0%' : n,
          transform: `scale(${s})`,
          transformOrigin: o && t === 1 ? '50% 0%' : n,
          ...a,
        }}
      />
      <span aria-hidden={!0} className={'mk-outline'} />
    </span>
  );
}
export function MarketFile({
  name: e,
  kind: t,
  icon: n,
  thumb: r,
  on: i,
  delay: a = 0,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <MarketReveal on={i} delay={a} className={'mk-file'}>
      <span className={'mk-file-thumb'}>
        {translateShowcaseNode(
          showcaseCopy,
          r ?? <ShowcaseIcon name={n} className={'text-ink-3 size-5'} />
        )}
      </span>
      <span className={'min-w-0'}>
        <span
          className={
            'text-ink block truncate text-[14px] leading-tight font-medium'
          }
        >
          {translateShowcaseNode(showcaseCopy, e)}
        </span>
        <span
          className={'text-ink-3 mt-0.5 block truncate text-[12px] leading-4'}
        >
          {translateShowcaseNode(showcaseCopy, t)}
        </span>
      </span>
      <ShowcaseIcon name={n} className={'text-ink-3 size-4'} />
    </MarketReveal>
  );
}
export function MarketStatus({ done: e, busy: t, result: n, meta: r }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div
      className={
        'text-ink-3 flex h-7 min-w-0 items-center gap-2 px-1 text-[14px]'
      }
    >
      <CompletionMark done={e} className={'size-4 shrink-0'} />
      <span className={'swap min-w-0 truncate'}>
        {translateShowcaseNode(
          showcaseCopy,
          e ? (
            <span key={'done'} className={'swap-in truncate'}>
              {translateShowcaseNode(showcaseCopy, n)}
            </span>
          ) : (
            <span key={'busy'} className={'truncate'}>
              {translateShowcaseNode(showcaseCopy, t)}
            </span>
          )
        )}
      </span>
      {translateShowcaseNode(
        showcaseCopy,
        r ? (
          <span
            className={
              'num text-ink-4 ml-auto shrink-0 text-[13px] transition-opacity duration-300 max-sm:hidden'
            }
            style={{
              opacity: +!!e,
            }}
          >
            {translateShowcaseNode(showcaseCopy, r)}
          </span>
        ) : null
      )}
    </div>
  );
}
export function MarketBrandChip({ brand: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return e === 'lisiere' ? (
    <span
      aria-hidden={!0}
      className={
        'ref-logo overflow-hidden bg-[#F2EEE6] px-[0.32em] leading-[0] text-[#1C1A17]'
      }
    >
      <LisiereLogo className={'block h-full w-auto py-[0.17em]'} />
    </span>
  ) : (
    <svg viewBox={'0 0 44 13'} aria-hidden={!0} className={'ref-logo'}>
      <rect width={'44'} height={'13'} rx={'1'} fill={'#F3EEE6'} />
      <text
        x={'22'}
        y={'8.7'}
        textAnchor={'middle'}
        fill={'#2B2522'}
        fontFamily={"'Instrument Serif', Georgia, serif"}
        fontSize={'5.4'}
        letterSpacing={'0.9'}
      >
        {translateShowcaseNode(showcaseCopy, 'AUBRAC')}
      </text>
    </svg>
  );
}
export const ru = {
  lisiere: 'LISI\xC8RE',
  aubrac: 'Maison Aubrac',
};
export function MarketPrompt({
  t: e,
  workflow: t,
  brand: n,
  prompt: r,
  doneAt: i,
  busy: a,
  result: o,
  meta: s,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div className={'flex flex-col gap-2'}>
      <MarketReveal on={e >= 60} className={'flex justify-end'}>
        <p
          className={
            'text-ink max-w-[94%] rounded-[18px] bg-[var(--secondary)] px-4 py-2 text-[14px] leading-[22px] shadow-[var(--shadow-minimal)] sm:max-w-[80%] sm:text-[15px] sm:leading-6'
          }
        >
          <span className={'ref ref-violet'}>
            <ShowcaseIcon name={'nodes'} className={'ref-icon'} />
            {translateShowcaseNode(showcaseCopy, t)}
          </span>
          {translateShowcaseNode(showcaseCopy, ' ')}
          <span className={'ref ref-amber'}>
            <MarketBrandChip brand={n} />
            <span className={'sr-only'}>
              {translateShowcaseNode(showcaseCopy, ru[n])}
            </span>
          </span>
          {translateShowcaseNode(showcaseCopy, ' ')}
          {translateShowcaseNode(showcaseCopy, r)}
        </p>
      </MarketReveal>
      <MarketReveal on={e >= 260}>
        <MarketStatus done={e >= i} busy={a} result={o} meta={s} />
      </MarketReveal>
    </div>
  );
}
export const au = {
  frames: 400,
  strip: 700,
  copy: 900,
  rows: 1200,
  chips: 1750,
  checks: 2100,
  done: 2300,
};
export const ou = [0, 2800, 4700];
export const su = 6800;
export const cu = [
  {
    key: 'feed',
    label: U.formats[0],
    frame: 'aspect-[4/5]',
    focus: ['50% 30%', '50% 25%', '50% 55%'],
  },
  {
    key: 'stories',
    label: U.formats[1],
    frame: 'aspect-[9/16]',
    focus: ['14% 50%', '24% 50%', '58% 50%'],
  },
  {
    key: 'square',
    label: U.formats[2],
    frame: 'aspect-square',
    focus: ['50% 22%', '50% 30%', '50% 60%'],
  },
];
export const lu = (e) => ou.reduce((t, n, r) => (e >= n ? r : t), 0);
export function CampaignAsset({ format: e, moment: t, t: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let r = Yl[t] ?? Yl[0],
    i = r?.tone === 'light',
    a = r?.place ?? 'top';
  return (
    <span
      className={
        'mk-deck relative block size-full overflow-hidden rounded-[10px] bg-[#D8CDBE]'
      }
    >
      {translateShowcaseNode(
        showcaseCopy,
        Yl.map((n, r) => (
          <span
            key={n.key}
            className={
              'absolute inset-0 transition-[opacity,filter] duration-500 ease-[var(--ease)]'
            }
            style={{
              opacity: +(r === t),
              filter: r === t ? 'none' : 'blur(6px)',
            }}
          >
            <MarketImage
              asset={n.asset}
              focus={e.focus[r]}
              className={'size-full'}
            />
          </span>
        ))
      )}
      <MarketReveal
        on={n >= au.frames + 500}
        className={`absolute flex flex-col gap-[2cqw] ${a === 'left' ? 'top-[7cqw] left-[5cqw] max-w-[33cqw] items-start text-left' : a === 'right' ? 'top-[8cqw] right-[6cqw] max-w-[42cqw] items-end text-right' : 'inset-x-0 top-[9cqw] items-center px-[6cqw] text-center'}`}
        style={{
          color: i ? '#F3EEE6' : '#14110F',
        }}
      >
        <span
          className={`mk-caps ${a === 'left' ? 'text-[max(5px,4cqw)]' : 'text-[max(6px,5cqw)]'}`}
        >
          {translateShowcaseNode(showcaseCopy, 'Maison Aubrac')}
        </span>
        <span
          className={`mk-serif leading-[1.02] ${a === 'left' ? 'text-[max(9px,7.5cqw)]' : 'text-[max(11px,10cqw)]'}`}
        >
          <BlurSwap value={r?.headline ?? ''} />
        </span>
      </MarketReveal>
      <span aria-hidden={!0} className={'mk-outline rounded-[10px]'} />
    </span>
  );
}
